import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Grid, Paper, CircularProgress, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import { useImage } from '../../hooks/useImage';

/**
 * Image Editor Component
 * A reusable component for editing images using AI
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Optional project ID for saving images
 * @param {string} props.initialImageUrl - URL of the image to edit
 * @param {Function} props.onImageEdited - Optional callback when image is edited
 * @param {Function} props.onImageSaved - Optional callback when image is saved to project
 * @param {boolean} props.showSaveOption - Whether to show the save option (default: true)
 * @param {Object} props.defaultOptions - Default options for image editing
 * @param {boolean} props.showFullControls - Whether to show all controls (default: true)
 * @param {string} props.initialPrompt - Initial value for the prompt field
 */
const ImageEditor = ({ 
  projectId,
  initialImageUrl,
  onImageEdited,
  onImageSaved,
  showSaveOption = true,
  defaultOptions = {},
  showFullControls = true,
  initialPrompt = ''
}) => {
  const { 
    loading, 
    generatedImages,
    editImage,
    saveImageToProject, 
    clearGeneratedImages 
  } = useImage();
  
  const [prompt, setPrompt] = useState(initialPrompt);
  const [imageUrl, setImageUrl] = useState(initialImageUrl || '');
  const [maskUrl, setMaskUrl] = useState('');
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    model: 'dall-e-2',  // dall-e-2 is the only model supporting edits currently
    size: '1024x1024',
    n: 1,
    ...defaultOptions
  });

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const handleImageUrlChange = (e) => {
    setImageUrl(e.target.value);
  };

  const handleMaskUrlChange = (e) => {
    setMaskUrl(e.target.value);
  };

  const handleOptionChange = (key, value) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleEditImage = async () => {
    if (!prompt.trim() || !imageUrl.trim()) return;
    
    // Clear any previous errors
    setError(null);
    
    try {
      const result = await editImage(
        imageUrl, 
        prompt, 
        maskUrl.trim() ? maskUrl : null, 
        options
      );
      
      // If there's a callback for when the image is edited
      if (onImageEdited) {
        onImageEdited(result);
      }
    } catch (error) {
      console.error('Failed to edit image:', error);
      
      // Handle common errors
      if (error.response) {
        // Server responded with an error status
        const status = error.response.status;
        const errorData = error.response.data || {};
        
        if (status === 401) {
          setError("API key authentication failed. Please check your OpenAI API key.");
        } else if (status === 400) {
          setError(errorData.message || "Bad request. Please check your image, mask, and prompt.");
        } else if (status === 429) {
          setError("Rate limit exceeded. Please try again later.");
        } else {
          setError(errorData.message || "An error occurred while editing the image.");
        }
      } else if (error.message === 'OpenAI API key is not configured') {
        setError("OpenAI API key is not configured. Please add your API key in the server settings.");
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    }
  };

  const handleSaveImage = async (editedImageUrl) => {
    if (!projectId) return;
    
    // Clear any previous errors
    setError(null);
    
    try {
      const savedImage = await saveImageToProject(projectId, editedImageUrl, prompt, options);
      if (onImageSaved) {
        onImageSaved(savedImage);
      }
    } catch (error) {
      console.error('Failed to save image:', error);
      
      // Handle common errors
      if (error.response) {
        // Server responded with an error status
        const errorData = error.response.data || {};
        setError(errorData.message || "Failed to save image to project.");
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred while saving the image.");
      }
    }
  };

  const handleClearImages = () => {
    clearGeneratedImages();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
          >
            {error}
          </Alert>
        </Box>
      )}
      
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Image URL to Edit"
          placeholder="URL of the image to edit..."
          value={imageUrl}
          onChange={handleImageUrlChange}
          variant="outlined"
          disabled={loading}
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          label="Mask URL (Optional)"
          placeholder="URL of the mask image (transparent areas will be edited)..."
          value={maskUrl}
          onChange={handleMaskUrlChange}
          variant="outlined"
          disabled={loading}
          sx={{ mb: 2 }}
          helperText="The mask must be a PNG with transparent areas where edits should be applied"
        />
        
        <TextField
          fullWidth
          label="Edit Instructions"
          placeholder="Describe how you want to edit the image..."
          value={prompt}
          onChange={handlePromptChange}
          multiline
          rows={3}
          variant="outlined"
          disabled={loading}
        />
      </Box>
      
      {showFullControls && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Size</InputLabel>
              <Select
                value={options.size}
                onChange={(e) => handleOptionChange('size', e.target.value)}
                label="Size"
                disabled={loading}
              >
                <MenuItem value="1024x1024">1024x1024 (Square)</MenuItem>
                <MenuItem value="512x512">512x512 (Small)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Quantity</InputLabel>
              <Select
                value={options.n}
                onChange={(e) => handleOptionChange('n', e.target.value)}
                label="Quantity"
                disabled={loading}
              >
                {[1, 2, 3, 4].map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEditImage}
          disabled={loading || !prompt.trim() || !imageUrl.trim()}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? 'Editing...' : 'Edit Image'}
        </Button>
        
        {generatedImages.length > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearImages}
            disabled={loading}
          >
            Clear Results
          </Button>
        )}
      </Box>
      
      {generatedImages.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Edited Images
          </Typography>
          
          <Grid container spacing={2}>
            {generatedImages.map((img, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper elevation={2} sx={{ p: 1, position: 'relative' }}>
                  <img 
                    src={img.url} 
                    alt={`Edited image ${index + 1}`}
                    style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
                  />
                  
                  {showSaveOption && projectId && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveImage(img.url)}
                        disabled={loading}
                      >
                        Save to Project
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      {initialImageUrl && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Original Image
          </Typography>
          <Paper elevation={1} sx={{ p: 1, maxWidth: 300 }}>
            <img 
              src={initialImageUrl} 
              alt="Original image"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ImageEditor;